import React, { useContext } from 'react';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { TextInputField } from '../../../../../shared/form/inputs/TextInputField';
import { Td, Tr, TBody, THead } from '../../../../../shared/Table';
import {
  GroupedTh,
  GroupedThBorderLeft,
  GroupedThBorderRight,
  GroupingTh,
  TdBorderLeft,
  TdBorderRight,
} from '../../../../../shared/SplitTable';
import { DesignFormDefaultsContext } from '../../../../designs/DesignFormDefaultsContext';
import { UnitFormModel } from '../unitFormModel';
import { RoomsHeatLossInputField } from './RoomsHeatLossInputField';
import { RoomsHeatLossTableColumnHeaderContextMenu } from './RoomsHeatLossTableColumnHeaderContextMenu';

type Props = {
  unit: UnitFormModel;
  filteredManifoldId: string | null;
  fillDownColumn: (columnFieldName: string) => () => void;
  roomHeatLossDetailsFieldName: (manifoldIndex: number, roomIndex: number, fieldName: string) => string;
};

export const RoomsWallTable = ({ unit, filteredManifoldId, fillDownColumn, roomHeatLossDetailsFieldName }: Props) => {
  const wallElements = assertNotNull(unit.heatLossDetails).wallElements;
  const { unitWallElements, setUnitWallElements } = assertNotNull(useContext(DesignFormDefaultsContext));

  const roomWallHeatLossDetailsFieldName = (
    manifoldIndex: number,
    roomIndex: number,
    wallElementIndex: number,
    fieldName: string,
  ) => roomHeatLossDetailsFieldName(manifoldIndex, roomIndex, `walls.${wallElementIndex}.${fieldName}`);

  const onExternalAirTemperatureChange = (
    manifoldIndex: number,
    roomIndex: number,
    wallElementIndex: number,
    newValue: string,
  ) => {
    if (unitWallElements) {
      const newUnitWallElements = [...unitWallElements];
      newUnitWallElements[wallElementIndex].manifoldRoomValues[manifoldIndex][roomIndex] = newValue;
      setUnitWallElements(newUnitWallElements);
    }
  };

  return (
    <>
      <THead>
        <Tr>
          {wallElements.map((wallElement, index) => (
            <GroupingTh key={index} colSpan={5}>
              {wallElement.name || 'Unnamed Wall Element'}
            </GroupingTh>
          ))}
        </Tr>
        <Tr>
          {wallElements.map((wallElement, index) => (
            <React.Fragment key={index}>
              <GroupedThBorderLeft>
                <RoomsHeatLossTableColumnHeaderContextMenu
                  fillDownColumn={fillDownColumn(`walls.${index}.outsideWallLengthInMetres`)}
                >
                  Outside Wall Length (m)
                </RoomsHeatLossTableColumnHeaderContextMenu>
              </GroupedThBorderLeft>
              <GroupedTh>
                <RoomsHeatLossTableColumnHeaderContextMenu
                  fillDownColumn={fillDownColumn(`walls.${index}.additionalWallAreaInMetresSquared`)}
                >
                  Additional Wall Area (m<sup>2</sup>)
                </RoomsHeatLossTableColumnHeaderContextMenu>
              </GroupedTh>
              <GroupedTh>
                <RoomsHeatLossTableColumnHeaderContextMenu
                  fillDownColumn={fillDownColumn(`walls.${index}.windowAreaInMetresSquared`)}
                >
                  Window Area (m<sup>2</sup>)
                </RoomsHeatLossTableColumnHeaderContextMenu>
              </GroupedTh>
              <GroupedTh>
                <RoomsHeatLossTableColumnHeaderContextMenu
                  fillDownColumn={fillDownColumn(`walls.${index}.doorAreaInMetresSquared`)}
                >
                  Door Area (m<sup>2</sup>)
                </RoomsHeatLossTableColumnHeaderContextMenu>
              </GroupedTh>
              <GroupedThBorderRight>
                <RoomsHeatLossTableColumnHeaderContextMenu
                  fillDownColumn={fillDownColumn(`walls.${index}.externalAirTemperatureInDegreesCelsius`)}
                >
                  External Air Temp. °C
                </RoomsHeatLossTableColumnHeaderContextMenu>
              </GroupedThBorderRight>
            </React.Fragment>
          ))}
        </Tr>
      </THead>
      <TBody>
        {unit.manifolds
          .map((manifold, manifoldIndex) => ({ manifold, manifoldIndex }))
          .filter(({ manifold }) => filteredManifoldId === null || manifold.id === filteredManifoldId)
          .map(({ manifold, manifoldIndex }) =>
            manifold.rooms == null
              ? null
              : manifold.rooms.map((room, roomIndex) => (
                  <Tr key={`${manifoldIndex}-${roomIndex}`}>
                    {wallElements.map((wallElement, wallElementIndex) => (
                      <React.Fragment key={wallElementIndex}>
                        <TdBorderLeft>
                          <TextInputField
                            name={roomWallHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              wallElementIndex,
                              'outsideWallLengthInMetres',
                            )}
                            label={null}
                          />
                        </TdBorderLeft>
                        <Td>
                          <RoomsHeatLossInputField
                            name={roomWallHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              wallElementIndex,
                              'additionalWallAreaInMetresSquared',
                            )}
                            referenceFieldName={roomWallHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              wallElementIndex,
                              'outsideWallLengthInMetres',
                            )}
                            label={null}
                          />
                        </Td>
                        <Td>
                          <RoomsHeatLossInputField
                            name={roomWallHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              wallElementIndex,
                              'windowAreaInMetresSquared',
                            )}
                            referenceFieldName={roomWallHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              wallElementIndex,
                              'outsideWallLengthInMetres',
                            )}
                            label={null}
                          />
                        </Td>
                        <Td>
                          <RoomsHeatLossInputField
                            name={roomWallHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              wallElementIndex,
                              'doorAreaInMetresSquared',
                            )}
                            referenceFieldName={roomWallHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              wallElementIndex,
                              'outsideWallLengthInMetres',
                            )}
                            label={null}
                          />
                        </Td>
                        <TdBorderRight>
                          <RoomsHeatLossInputField
                            name={roomWallHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              wallElementIndex,
                              'externalAirTemperatureInDegreesCelsius',
                            )}
                            referenceFieldName={roomWallHeatLossDetailsFieldName(
                              manifoldIndex,
                              roomIndex,
                              wallElementIndex,
                              'outsideWallLengthInMetres',
                            )}
                            label={null}
                            onChange={newValue =>
                              onExternalAirTemperatureChange(manifoldIndex, roomIndex, wallElementIndex, newValue)
                            }
                          />
                        </TdBorderRight>
                      </React.Fragment>
                    ))}
                  </Tr>
                )),
          )}
      </TBody>
    </>
  );
};
